"use client";

import React from "react";
import { ArrowRight, CircleCheck, Activity, ShieldCheck } from "lucide-react";
import { RadialBarChart, RadialBar, PolarGrid, PolarRadiusAxis, Label } from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartConfig } from "@/components/ui/chart";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  browser: "safari",
  visitors: 99,
  fill: "var(--color-chart-1)"
}];
const chartConfig = {
  visitors: {
    label: "Yield"
  },
  safari: {
    label: "Safari",
    color: "hsl(var(--chart-1))"
  }
} satisfies ChartConfig;
export default function DigitalExcellenceForLifeSciences() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="56300" id="precision-beverage-manufacturing" ref={sectionRef} className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-32">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--primary)/0.03)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--primary)/0.03)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-40" />
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-96 h-96 bg-chart-1/10 rounded-full blur-3xl opacity-40" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-5 lg:gap-8 items-center">
          
          {}
          <div className={`lg:col-span-3 flex flex-col gap-8 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-xs font-mono font-medium text-primary backdrop-blur-sm w-fit uppercase tracking-widest">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                FDA-REGISTERED | LOW-MOQ
              </div>
              
              <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl lg:text-6xl xl:text-7xl font-sans">
                Precision Crafting <br />
                <span className="bg-gradient-to-r from-primary to-chart-2 bg-clip-text text-transparent">
                  for Wellness Brands
                </span>
              </h1>
              
              <p className="max-w-2xl text-xl text-muted-foreground leading-relaxed font-sans">
                Custom RTD Co. delivers professional contract manufacturing for functional 
                beverages. Specialized formulation and counter-pressure canning 
                designed for premium scale and technical compliance.
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="h-14 px-10 text-base font-semibold rounded-full shadow-xl shadow-primary/20 bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300">
                <Link to="/ContactPage">
                  Get a Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-14 px-10 text-base font-semibold rounded-full border-primary/20 bg-background/30 backdrop-blur-md hover:bg-primary/5 hover:border-primary/40 transition-all duration-300">
                <Link to="/ServicesPage">Explore Services</Link>
              </Button>
            </div>

            <div className="flex items-center gap-8 pt-4 text-sm font-mono text-muted-foreground uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-5 w-5 text-primary" />
                <span>FDA Facility</span>
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-5 w-5 text-primary" />
                <span>GMP Compliant</span>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-2 relative transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
            {}
            <div className="relative rounded-xl overflow-hidden border border-white/5 shadow-2xl bg-black/20 backdrop-blur-sm">
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent z-10" />
              <img src="https://customrtd.com/wp-content/uploads/2026/09/scientist_interface_overlay-optimized.webp" alt="Precision canning line production monitoring" className="w-full h-auto object-cover opacity-90 mix-blend-luminosity min-h-[400px]" width={900} height={676} loading="eager" fetchPriority="high" />
              
              {}
              <div className="absolute bottom-6 left-6 right-6 z-20">
                <Card className="bg-card/80 backdrop-blur-xl border-white/5 shadow-2xl rounded-xl">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-semibold tracking-tight">Production Yield</CardTitle>
                      <ShieldCheck className="h-5 w-5 text-primary" />
                    </div>
                    <CardDescription className="text-xs font-mono uppercase tracking-tighter">Real-time batch monitoring</CardDescription>
                  </CardHeader>
                  <CardContent className="flex items-center gap-6 pb-6">
                    <div className="h-[120px] w-[120px] relative">
                      <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[250px]">
                        <RadialBarChart data={chartData} startAngle={90} endAngle={450} innerRadius={45} outerRadius={70}>
                          <PolarGrid gridType="circle" radialLines={false} stroke="none" className="first:fill-muted/20 last:fill-background/40" polarRadius={[50, 40]} />
                          <RadialBar dataKey="visitors" background cornerRadius={10} />
                          <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                            <Label content={({
                            viewBox
                          }) => {
                            if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                              return <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                                      <tspan x={viewBox.cx} y={viewBox.cy} className="fill-foreground text-2xl font-bold tracking-tighter">
                                        {chartData[0].visitors}%
                                      </tspan>
                                      <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 16} className="fill-muted-foreground text-[10px] font-mono uppercase">
                                        Efficiency
                                      </tspan>
                                    </text>;
                            }
                          }} />
                          </PolarRadiusAxis>
                        </RadialBarChart>
                      </ChartContainer>
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between text-xs font-mono">
                        <span className="text-muted-foreground">LINE STATUS</span>
                        <span className="text-primary font-bold flex items-center gap-1">
                          <Activity className="h-3 w-3" /> ACTIVE
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-muted/30 overflow-hidden">
                        <div className="h-full bg-primary w-[99%]" />
                      </div>
                      <p className="text-[10px] text-muted-foreground font-sans leading-tight">
                        Counter-pressure systems calibrated. Batch #402 proceeding with zero deviation.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {}
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/20 rounded-full blur-2xl -z-10" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-chart-2/20 rounded-full blur-2xl -z-10" />
          </div>
        </div>
      </div>
    </section>;
}